<script setup lang="ts">
import OfferingWizard from '@/components/OfferingWizard.vue'
import type { OfferingKind } from '@/types'

definePage({
  meta: { requiresAuth: true, centerConfig: true },

  /*
   * Qué item del menú queda marcado, resuelto ANTES de entrar.
   *
   * `navActiveLink` es el escape para las páginas sin item propio, pero acá
   * depende del tipo que viene por query y `meta` es estático. Va en el guard y
   * no en el `setup` porque `VerticalNavGroup` decide si el grupo está abierto
   * **una sola vez por navegación** (§18.5): asignándolo después, el grupo
   * Offerings evaluaba sin match y se cerraba al entrar.
   *
   * 🪤 Se escribe en el registro y no solo en `to.meta`. Ese `meta` es un objeto
   * NUEVO —Vue Router lo arma fusionando los registros con `mergeMetaFields`,
   * que reduce sobre `{}`— y el matcher de la plantilla (`isNavLinkActive`)
   * compara contra el registro. Escribir solo en la copia es lo que hacía esta
   * pantalla, y por eso entraba con el grupo cerrado y nada marcado.
   *
   * 🪤 Y solo en el ÚLTIMO, no en todos los `matched`: los registros intermedios
   * que arma el ruteo por archivos no tienen componente y los comparten todas
   * las pantallas del centro, así que la marca quedaba pegada ahí y encendía dos
   * items a la vez. Ver `utils/navActiveLink.ts`, que es lo mismo para el
   * `setup`.
   *
   * Va inline y no por ese helper porque `definePage` extrae este guard fuera del
   * módulo: acá adentro los imports del bloque `script setup` no existen.
   *
   * 🪤 Y en este comentario no se puede escribir ese nombre entre signos de menor
   * y mayor: `vite-plugin-vue-inspector` compila el bloque extraído como si fuera
   * un template, y ahí una etiqueta suelta lo tumba con "Element is missing end
   * tag" — un 500 del dev server y la página en blanco.
   */
  beforeEnter: to => {
    const target = to.query.kind === 'product'
      ? 'dive-centers-id-offerings-rentals'
      : 'dive-centers-id-offerings-services'

    to.meta.navActiveLink = target

    const leaf = to.matched[to.matched.length - 1]

    if (leaf)
      leaf.meta.navActiveLink = target

    return true
  },
})

const route = useRoute('dive-centers-id-offerings-new')
const id = String(route.params.id)

/*
 * El tipo viene de la pantalla desde la que se entró. `service` por defecto:
 * si alguien llega con la URL a mano, el alta más común es la que gana.
 */
const kind = computed<OfferingKind>(() => (route.query.kind === 'product' ? 'product' : 'service'))
</script>

<template>
  <div
    class="mx-auto"
    style="max-width: 1100px;"
  >
    <OfferingWizard
      :dc-id="id"
      :kind="kind"
    />
  </div>
</template>
